import { useBuilderContext } from "@Context/game/builder/builder-context";
import "@pixi/events";
import { Graphics as PixiGraphics } from "@pixi/graphics";
import { Container, Graphics, Sprite, Text } from "@pixi/react";
import Football from "@Resources/game/builder/football.png";
import Dot2 from "@Resources/game/builder/dot2.png";
import Dot3 from "@Resources/game/builder/dot3.png";
import Chevron from "@Resources/game/builder/chevron.png";
import { useCallback, useEffect, useState } from "react";
import { getPointAtTime, drawDashedLine } from "./functions/utilities";
import { isEmpty, isNumber } from "lodash-es";
import { Point3 } from "@Shared/types/ScenarioTypes";
import { BallPoint } from "./models/builder-data-types";
import { ColorSource } from "@pixi/core";
const defaultColor = 0x00FFFF;

const TrackedBall = (props: { 
  size?: number; 
  color?: ColorSource;
  opacity?: number;
  onDragStart?: () => void; 
  onDragged?: (pos: { x: number; y: number }) => void 
  frames: BallPoint[],
  showHeight?: boolean,
  showDebug?: boolean,
  showSegments?: boolean
}) => {
  const { state, dispatch } = useBuilderContext();

  const [position, setPosition] = useState<Point3>({ ...state.clip.scenario.ball.initialPosition, z: 0 });
  const [possessorPosition, setPossessorPosition] = useState<{ x: number; y: number } | undefined>(undefined);
  const [currentSegmentIndex, setCurrentSegmentIndex] = useState(0);

  useEffect(() => {
    if (!state.clip.scenario.ball.segments) return;
    let newSegmentIndex = state.clip.scenario.ball.segments.findIndex((s) => (s.startTime <= state.time && s.endTime >= state.time) || s.startTime >= state.time);
    if (newSegmentIndex === -1) {
      newSegmentIndex = state.clip.scenario.ball.segments.length - 1;
    }
    const segment = state.clip.scenario.ball.segments[newSegmentIndex];
    setCurrentSegmentIndex(newSegmentIndex);
    if (!segment || !isNumber(segment.fromPlayer)) return;
    const newPossessor = state.clip.scenario.players.get(segment.fromPlayer);
    if (!newPossessor) return;
    const pos = getPointAtTime(newPossessor.points, state.time, newPossessor.beziers);
    setPossessorPosition(pos.position);
  }, [state.clip.scenario.ball.segments, state.clip.scenario.players, state.time]);

  useEffect(() => {
    if (!props.showDebug || !state.clip.scenario.ball.segments) return;
    console.log("Segment", state.clip.scenario.ball.segments[currentSegmentIndex], "Next segment", state.clip.scenario.ball.segments[currentSegmentIndex + 1]);
  }, [state.clip.scenario.ball.segments, currentSegmentIndex, props.showDebug]);

  useEffect(() => {

    // Find the ball points around the current time
    const fromPoint = props.frames.findLast(p => p.t <= state.time);
    const toPoint = props.frames.find(p => p.t >= state.time);
    if (!fromPoint || !toPoint) {
      setPosition({ ...state.clip.scenario.ball.initialPosition, z: 0 });
      return;
    }

    const newPossessor = state.clip.scenario.players.get(parseInt(fromPoint.possessor || "-1"));
    if (newPossessor) {
      const pos = getPointAtTime(newPossessor.points, state.time, newPossessor.beziers);
      setPossessorPosition(pos.position);
    }

    const fromPos = fromPoint.position;
    const toPos = toPoint.position;
    const t = (state.time - fromPoint.t) / (toPoint.t - fromPoint.t);
    const position = {
      x: fromPos.x + (toPos.x - fromPos.x) * t,
      y: fromPos.y + (toPos.y - fromPos.y) * t,
      z: fromPos.z + (toPos.z - fromPos.z) * t
    };
    setPosition(position);
  }, [props.frames, state.time]);

  const drawPath = useCallback(
    (g: PixiGraphics) => {
      g.clear();

      if (isEmpty(state.clip.scenario.ball.segments)) {
        for (let i = 0; i < props.frames.length - 1; i++) {
          const alpha = Math.max(0, 1 - Math.abs(state.time - props.frames[i].t) / 2);
          const point = props.frames[i];
          const nextPoint = props.frames[i + 1];
          if (!nextPoint) continue;
          g.lineStyle(3 * state.spriteScale, props.color || 0x00FFFF, alpha);
          g.moveTo(point.position.x, point.position.y);
          g.lineTo(nextPoint.position.x, nextPoint.position.y);
        }
        return;
      }
      
      // Group frames by segments
      for (let segmentIndex = 0; segmentIndex < state.clip.scenario.ball.segments.length; segmentIndex++) {
        const segment = state.clip.scenario.ball.segments[segmentIndex];
        const segmentFrames = props.frames.filter(frame => 
          frame.t >= segment.startTime && frame.t <= segment.endTime
        );
        
        if (segmentFrames.length === 0) continue;
      
        const isPass = segment.fromPlayer !== undefined && 
                      segment.toPlayer !== undefined && 
                      segment.fromPlayer !== segment.toPlayer;
        
        const alpha = segmentIndex === currentSegmentIndex ? 1 : 0.25;
        const color = props.color || 0x00FFFF;
        const opacity = props.opacity || 1;
        const thickness = 3 * state.spriteScale;
        
        if (isPass && props.showSegments) {
          // Draw dashed line for passes with uniform spacing
          if (segmentFrames.length >= 2) {
            const startPoint = segmentFrames[0];
            const nextSegment = state.clip.scenario.ball.segments[segmentIndex + 1];
            let endPoint = segmentFrames[segmentFrames.length - 1].position;
            if(nextSegment) {
              endPoint = nextSegment.startPoint;
            }
            g.lineStyle(thickness, color, alpha * opacity);
            
            drawDashedLine(
              g,
              startPoint.position.x,
              startPoint.position.y,
              endPoint.x,
              endPoint.y,
              6 * state.spriteScale,
              6 * state.spriteScale
            );
          }
        } else {
          // Draw solid line for non-passes
          for (let i = 0; i < segmentFrames.length - 1; i++) {
            
            const point = segmentFrames[i];
            const nextPoint = segmentFrames[i + 1];
            
            if (!nextPoint) continue;
            
            g.lineStyle(thickness * 1.5, color, alpha * opacity);
            g.moveTo(point.position.x, point.position.y);
            g.lineTo(nextPoint.position.x, nextPoint.position.y);
          }
          if( segmentIndex < state.clip.scenario.ball.segments.length - 1) {
            g.lineStyle(thickness * 1.5, color, alpha * opacity);
            const nextSegment = state.clip.scenario.ball.segments[segmentIndex + 1];
            if(!nextSegment) continue;
            g.lineTo(nextSegment.startPoint.x, nextSegment.startPoint.y);
          }
        }
      }
    },
    [state.clip.scenario.ball.segments, props.frames, state.spriteScale, state.time, currentSegmentIndex]
  );

  return (
    <Container>
      <Sprite
        image={Football}
        x={position.x}
        y={position.y}
        tint={props.color || 0x00FFFF}
        alpha={props.opacity || 1}
        anchor={0.5}
        width={(props.size || 32) * state.spriteScale}
        height={(props.size || 32) * state.spriteScale}
        eventMode={"passive"}
      />
      {state.options.showBallPath && <Graphics draw={drawPath} />}
      {props.showSegments && state.clip.scenario.ball.segments?.map((segment, index) =>
        <Sprite
          key={index}
          image={(index === currentSegmentIndex || index === currentSegmentIndex + 1) ? Dot3 : Dot2}
          x={segment.startPoint.x}
          y={segment.startPoint.y}
          alpha={((index === currentSegmentIndex || index === currentSegmentIndex + 1) ? 1 : 0.5) * (props.opacity || 1)}
          tint={props.color || 0x00FFFF}
          anchor={0.5}
          width={16 * state.spriteScale}
          height={16 * state.spriteScale}
        />)}
      {props.showSegments && state.clip.scenario.ball.segments?.length > 0 &&
        <Sprite
          key={state.clip.scenario.ball.segments.length}
          image={currentSegmentIndex === state.clip.scenario.ball.segments.length - 1 ? Dot3 : Dot2}
          x={state.clip.scenario.ball.segments[state.clip.scenario.ball.segments.length - 1].endPoint.x}
          y={state.clip.scenario.ball.segments[state.clip.scenario.ball.segments.length - 1].endPoint.y}
          alpha={(props.opacity || 1) * (currentSegmentIndex === state.clip.scenario.ball.segments.length - 1 ? 1 : 0.5)}
          tint={props.color || 0x00FFFF}
          anchor={0.5}
          width={16 * state.spriteScale}
          height={16 * state.spriteScale}
        />}
      {possessorPosition && <Sprite
        image={Chevron}
        x={possessorPosition?.x}
        y={possessorPosition?.y - 24 * state.spriteScale}
        tint={props.color || 0x00FFFF}
        alpha={props.opacity || 1}
        anchor={0.5}
        width={16 * state.spriteScale}
        height={16 * state.spriteScale}
      />}
      <Text text={currentSegmentIndex.toString()} x={10} y={24 * state.spriteScale}
        style={
          {
            align: "left",
            fill: "white",
            fontSize: 16 * state.spriteScale,
            fontWeight: "bold",
            letterSpacing: 0,
          } as any
        } />
      {props.showHeight && <Text text={position.z.toFixed(2)} x={position.x} y={position.y - 48 * state.spriteScale}
        style={
          {
            align: "center",
            fill: "white",
            fontSize: 16 * state.spriteScale,
            fontWeight: "bold",
            letterSpacing: 0,
          } as any
        } />}
      <Text text={state.time.toString()}
        x={10}
        y={10}
        style={
          {
            align: "left",
            fill: "white",
            fontSize: 16 * state.spriteScale,
            fontWeight: "bold",
            letterSpacing: 0,
          } as any
        } />
    </Container>
  );
};

export default TrackedBall;
